import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { Product } from '@/context/SupabaseInventoryContext';
import { toast } from '@/hooks/use-toast';

interface DuplicateGroup {
  key: string;
  products: Product[];
  type: 'barcode' | 'name';
}

interface DuplicateProductsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  duplicates: DuplicateGroup[];
  onDeleteProducts: (productIds: string[]) => Promise<void>;
}

export const DuplicateProductsModal: React.FC<DuplicateProductsModalProps> = ({
  open,
  onOpenChange,
  duplicates,
  onDeleteProducts
}) => {
  const [selectedForDeletion, setSelectedForDeletion] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  const handleToggleProduct = (productId: string) => {
    setSelectedForDeletion(prev => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    if (selectedForDeletion.size === 0) {
      toast({
        title: 'Aucune sélection',
        description: 'Veuillez sélectionner au moins un produit à supprimer',
        variant: 'destructive'
      });
      return;
    }

    if (!window.confirm(`Supprimer ${selectedForDeletion.size} produit(s) en double ?`)) {
      return;
    }

    setIsDeleting(true);
    try {
      await onDeleteProducts(Array.from(selectedForDeletion));
      toast({
        title: 'Suppression réussie',
        description: `${selectedForDeletion.size} produit(s) supprimé(s)`
      });
      setSelectedForDeletion(new Set());
      onOpenChange(false);
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer les produits',
        variant: 'destructive'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const formatPrice = (price: number) => new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR'
  }).format(price);

  const totalDuplicates = duplicates.reduce((sum, group) => sum + group.products.length, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Produits en double détectés
          </DialogTitle>
          <DialogDescription>
            {totalDuplicates} produit(s) en double trouvé(s). Sélectionnez ceux que vous souhaitez supprimer.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {duplicates.map((group, groupIndex) => (
            <div key={groupIndex} className="border rounded-lg p-4 bg-muted/20">
              <div className="mb-3 flex items-center justify-between">
                <div className="font-medium text-sm">
                  {group.type === 'barcode' ? (
                    <>Code-barres: <span className="font-mono">{group.key}</span></>
                  ) : (
                    <>Nom: {group.key}</>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {group.products.length} doublons
                </div>
              </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Nom</TableHead>
                      <TableHead>Code-barres</TableHead>
                      <TableHead>Quantité</TableHead>
                      <TableHead>Prix</TableHead>
                      <TableHead>Ajouté le</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {group.products.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedForDeletion.has(product.id)}
                            onCheckedChange={() => handleToggleProduct(product.id)}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{product.name}</TableCell>
                        <TableCell className="font-mono text-sm">{product.barcode || '-'}</TableCell>
                        <TableCell>{product.quantity}</TableCell>
                        <TableCell>{formatPrice(product.price)}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {new Date(product.created_at).toLocaleDateString('fr-FR')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-between items-center pt-4 border-t">
          <div className="text-sm text-muted-foreground">
            {selectedForDeletion.size > 0 && (
              <span>{selectedForDeletion.size} produit(s) sélectionné(s)</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteSelected}
              disabled={selectedForDeletion.size === 0 || isDeleting}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer la sélection
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
